import { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ArrowLeft, MapPin, Calendar, Trophy, Coins, Star, Filter, Sparkles, RotateCw, ArrowUp, ArrowDown, ArrowRightLeft } from "lucide-react";
import { getMatchScorecard, getMatchAIReport, generateMatchAIReport, getMatchPartnerships, getMatchNotes } from "../../../api/matches.api";
import useCreditConfirm from "../../../hooks/useCreditConfirm";
import CreditConfirmModal from "../../../components/wallet/CreditConfirmModal";
import api from "../../../api/axios";
import useLowBalanceToast from "../../../hooks/useLowBalanceToast";
import useAuthStore from "../../../store/auth.store";
import { useOfflineBundle } from "../../../hooks/useOfflineBundle";
import { useLiveMatch } from "../../../hooks/useLiveMatch";
import { Download, Trash2, CheckCircle2, Wifi, FileDown, Newspaper } from "lucide-react";
import WalletBadge from "../../../components/wallet/WalletBadge";
import QcLink from "../../../components/common/QcLink";
import MatchExtras from "../../admin-app/MatchExtras";
import { isHundred, formatInningsProgress, formatBowlerBalls, bowlerBallsColumnLabel, formatMaxLength, deriveOverBase } from "../../../utils/hundredDisplay";

export default function Scorecard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  // Backend-computed partnerships keyed by innings_number — see
  // /api/matches/.../partnerships/. Carries runs, balls, per-batter
  // contributions, and how each partnership ended. Falls back to the
  // legacy FOW-derived computation in InningsBlock if the endpoint
  // ever fails so the page still renders something.
  const [partnershipsByInnings, setPartnershipsByInnings] = useState({});
  // 2026-06-09 — chronological auto-derived match notes (drinks break,
  // rain, injuries, wickets, boundaries, innings transitions). Failure
  // is non-fatal — the rest of the page renders without the section.
  const [matchNotes, setMatchNotes] = useState([]);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);
  const [lastRefreshed, setLastRefreshed] = useState(null);
  // Auto-fires when any AI feature on this page debits Zencredits via
  // useCreditConfirm — the wallet_changed event drives checkAndWarn.
  const { toastMsg: lowBalToast, dismiss: dismissLowBal } = useLowBalanceToast();

  // Initial load + auto-refresh every 12s while match is IN_PROGRESS.
  // Stops polling once state becomes COMPLETED. Lets spectators (and
  // tweet-shared scorecard URLs) see live updates without manual refresh.
  useEffect(() => {
    let alive = true;
    let timer = null;
    const fetchOnce = (showLoader) => {
      if (showLoader) setLoading(true);
      // Fetch scorecard + partnerships in parallel so the page renders
      // in one paint. Partnerships failure is non-fatal — InningsBlock
      // falls back to FOW-derived partnerships if the map is empty.
      Promise.all([
        getMatchScorecard(id),
        getMatchPartnerships(id).catch(() => null),
        getMatchNotes(id).catch(() => null),
      ])
        .then(([scorecardRes, partnershipsRes, notesRes]) => {
          if (!alive) return;
          setData(scorecardRes.data);
          if (partnershipsRes?.data?.innings) {
            const byInn = {};
            for (const innBlock of partnershipsRes.data.innings) {
              byInn[innBlock.innings_number] = innBlock.partnerships || [];
            }
            setPartnershipsByInnings(byInn);
          }
          if (Array.isArray(notesRes?.data?.notes)) {
            setMatchNotes(notesRes.data.notes);
          }
          setError("");
          setLastRefreshed(new Date());
          // Schedule next poll only if match is still live
          const state = scorecardRes.data?.match?.state;
          if (state === "IN_PROGRESS") {
            timer = setTimeout(() => fetchOnce(false), 12000);
          }
        })
        .catch((e) => alive && setError(e?.response?.data?.detail || e.message || "Failed to load scorecard"))
        .finally(() => alive && setLoading(false));
    };
    fetchOnce(true);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
    };
  }, [id]);

  if (loading) return <Shell><div className="text-text-secondary">Loading scorecard…</div></Shell>;
  if (error) return (
    <Shell>
      <div className="bg-button-primary/10 border border-button-primary/30 rounded-lg p-4 text-button-primary text-sm">{error}</div>
    </Shell>
  );
  if (!data) return <Shell><div className="text-text-secondary">No data.</div></Shell>;

  const { match, team1, team2, stadium, match_type, competition, toss, playing_xi, innings, result, spatial } = data;

  return (
    <Shell>
      {lowBalToast && (
        <div className="mb-3 rounded-md border border-amber-500/40 bg-amber-500/10 text-amber-200 px-3 py-2 text-sm flex items-center justify-between gap-3">
          <span>⚠ {lowBalToast}</span>
          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => navigate("/account/topup")}
              className="text-[11px] uppercase tracking-wider px-2 py-1 rounded bg-amber-500 text-black hover:bg-amber-400"
            >
              Top up
            </button>
            <button
              onClick={dismissLowBal}
              className="text-amber-300 hover:text-amber-100 px-1"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        </div>
      )}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <button onClick={() => navigate("/cricket/matches")} className="flex items-center gap-1 text-sm text-text-secondary hover:text-text-primary transition-colors">
          <ArrowLeft className="w-4" /> Back to matches
        </button>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Live-refresh pill — shows when auto-refresh is active. */}
          {match.state === "IN_PROGRESS" && (
            <span className="flex items-center gap-1.5 text-[10px] uppercase tracking-wider px-2 py-1 rounded-full bg-rose-500/15 text-rose-300 border border-rose-500/30">
              <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />
              Live
              {lastRefreshed && (
                <span className="text-[9px] opacity-70 normal-case">
                  · {lastRefreshed.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", second: "2-digit" })}
                </span>
              )}
            </span>
          )}
          <WalletBadge />
          <OfflineBundleToggle matchId={id} />
          <LiveSubscribeIndicator matchId={id} matchState={match.state} />
          <PDFReportButton matchId={id} />
          <PressReportButton matchId={id} />
          {/* AI auto-tag — gated on the user's plan-granted features.
              Pricing v2 restricts to TRIAL + B2B; paid B2C tiers don't
              get the button (would 403 from the backend). */}
          {(() => {
            const u = useAuthStore.getState().user;
            const can = u?.is_staff || (u?.features || []).includes("ai_tagging");
            return can ? <AITagButton matchId={id} /> : null;
          })()}
          <button
            onClick={() => navigate(`/cricket/matches/${id}/edit-balls`)}
            className="text-sm px-3 py-1.5 rounded-md bg-blue-500/15 border border-blue-500/40 hover:bg-blue-500/25 text-blue-300"
            title="Pick an innings + edit any past ball"
          >
            Edit balls →
          </button>
          <QcLink matchId={id} variant="scorecard" label="QC →" />
          <button
            onClick={() => navigate(`/cricket/matches/${id}/control`)}
            className="text-sm px-3 py-1.5 rounded-md bg-bg-secondary border border-border-default hover:border-button-primary text-text-primary"
            title="Substitutions, warnings, penalties, conduct, edit log"
          >
            Match control →
          </button>
        </div>
      </div>

      {/* Match header */}
      <div className="bg-bg-primary border border-border-default rounded-2xl p-4 sm:p-6 mb-5">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-3">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] text-text-secondary">{match_type?.name || "Match"}</div>
            <h1 className="text-2xl sm:text-3xl font-extrabold mt-1">
              {team1?.name} <span className="text-text-secondary text-xl"> vs </span> {team2?.name}
            </h1>
          </div>
          <ResultBanner result={result} resultType={match.result_type} />
        </div>
        <div className="flex flex-wrap gap-x-6 gap-y-2 text-xs text-text-secondary">
          {match.match_date && <span className="flex items-center gap-1.5"><Calendar className="w-3.5" />{match.match_date}</span>}
          {stadium && <span className="flex items-center gap-1.5"><MapPin className="w-3.5" />{stadium.name}{stadium.city ? `, ${stadium.city}` : ""}</span>}
          {competition?.name && <span className="flex items-center gap-1.5"><Trophy className="w-3.5" />{competition.name}{competition.season ? ` · ${competition.season}` : ""}</span>}
          {/* CI_ID is internal archive metadata — gated to org users so
              B2C scorecards don't leak our import-source identifiers. */}
          {match.ci_id && useAuthStore.getState().user?.organization_id && (
            <span className="ml-auto font-mono text-[11px]">CI #{match.ci_id}</span>
          )}
        </div>
      </div>

      {/* AI report */}
      <AIReportSection matchId={id} />

      {/* Toss */}
      {toss && (
        <Section title="Toss" icon={<Coins className="w-4" />}>
          <p className="text-sm">
            <span className="text-text-primary font-semibold">{toss.won_by_name || "—"}</span> won the toss and elected to{" "}
            <span className="text-button-primary font-semibold">{toss.decision === "BAT" ? "bat" : "bowl"} first</span>.
          </p>
          {match?.floodlights_on != null && (
            <p className="text-xs text-text-secondary mt-1">
              Floodlights: <span className={match.floodlights_on ? "text-amber-300 font-semibold" : "text-text-primary"}>
                {match.floodlights_on ? "ON" : "OFF"}
              </span>
            </p>
          )}
        </Section>
      )}

      {/* 2026-06-09 — Match notes (auto-derived). Chronological feed of
          every significant event: wickets, boundaries, no-balls + free
          hits, penalties, DRS, drinks breaks (format-specific), rain
          interruptions, retired-hurt (player injury), innings start +
          end, match start + end. Drives off the backend's
          /match-notes/ endpoint so legacy matches also get a feed. */}
      {matchNotes.length > 0 && (
        <Section title="Match notes" icon={<Coins className="w-4" />}>
          <ol className="space-y-2 max-h-[60vh] overflow-y-auto pr-2">
            {matchNotes.map((n, i) => (
              <MatchNoteRow key={`${i}-${n.timestamp || ""}`} note={n} />
            ))}
          </ol>
        </Section>
      )}

      {/* Innings batting + bowling cards (cricinfo style: card + DNB + FOW + partnerships) */}
      {(innings || []).map((inn) => (
        <InningsBlock
          key={inn.innings_number}
          inn={inn}
          matchType={match_type}
          playingXI={playing_xi}
          partnerships={partnershipsByInnings[inn.innings_number]}
        />
      ))}

      {/* 2026-06-05 (v2) — match-wide partnerships section per founder
          reference image. Renders BOTH teams' partnerships side-by-side
          in a 2-column grid (stacks on phones). Each team column has
          its own header + a vertical stack of partnership rows. The
          per-innings partnership block inside each InningsBlock is
          hidden in favour of this comparison view. */}
      <MatchPartnershipsSection
        innings={innings || []}
        partnershipsByInnings={partnershipsByInnings}
      />

      {/* Wagon Wheel */}
      <WagonWheelSection spatial={spatial} innings={innings} />

      {/* Pitch Map */}
      <PitchMapSection spatial={spatial} innings={innings} />

      {/* Playing XI */}
      <Section title="Playing XI">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <XIList team={team1} rows={playing_xi?.[team1?.id] || []} />
          <XIList team={team2} rows={playing_xi?.[team2?.id] || []} />
        </div>
      </Section>

      <SubstitutionsSection subs={data.substitutions || []} team1={team1} team2={team2} />

      <FowSection innings={innings || []} />

      {/* Single commentary surface (was previously rendered twice —
          once by MatchExtras with the synthesised text, once by
          CommentarySection with the AI regenerate button). The text is
          now backfilled into Ball.auto_commentary so the AI-equipped
          section below shows the lines too — one block instead of
          two. */}

      {/* Pitch / weather summary */}
      {(match.pitch_report || (match.pitch_conditions && Object.keys(match.pitch_conditions).length > 0)) && (
        <Section title="Pitch & weather">
          {match.pitch_report && <p className="text-sm mb-2">{match.pitch_report}</p>}
          <div className="flex flex-wrap gap-2 text-xs text-text-secondary">
            {Object.entries(match.pitch_conditions || {}).map(([k, v]) => (
              <span key={k} className="px-2 py-1 rounded bg-bg-secondary border border-border-default">
                <span className="uppercase tracking-wider text-[10px]">{k}:</span>{" "}
                <span className="text-text-primary">{String(v)}</span>
              </span>
            ))}
          </div>
        </Section>
      )}

      {/* Law-event timeline */}
      <MatchEventsSection matchId={id} />

      {/* Ball-by-ball commentary feed */}
      <CommentarySection matchId={id} />
    </Shell>
  );
}

function CommentarySection({ matchId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(null);
  // Auto-refresh the commentary feed so edits/undos done in the live
  // BBP coding page show up here without a manual reload. Operators
  // reported a "gap" — stale rows lingering after an undo or a re-
  // generated commentary. Two triggers:
  //   1. Tab regains focus (visibilitychange) — operator just came
  //      back from coding screen.
  //   2. Polling every 20s while the tab is visible — covers cases
  //      where a coach is watching live without ever leaving the
  //      scorecard tab.
  useEffect(() => {
    if (!matchId) return undefined;
    let alive = true;
    let timer = null;

    const load = (initial = false) => {
      if (initial) setLoading(true);
      api.get(`/api/matches/${matchId}/commentary/`)
        .then((r) => alive && setRows(r.data?.results || []))
        .catch(() => {})
        .finally(() => alive && initial && setLoading(false));
    };

    load(true);

    const onVisible = () => {
      if (document.visibilityState === "visible") load(false);
    };
    document.addEventListener("visibilitychange", onVisible);

    const poll = () => {
      timer = setTimeout(() => {
        if (document.visibilityState === "visible") load(false);
        if (alive) poll();
      }, 20_000);
    };
    poll();

    return () => {
      alive = false;
      document.removeEventListener("visibilitychange", onVisible);
      if (timer) clearTimeout(timer);
    };
  }, [matchId]);
  // Per-ball commentary regenerate confirms a 3-credit charge before
  // each call. Cheap action, single confirmation per click — keeps the
  // UX snappy.
  const commentaryConfirm = useCreditConfirm("commentary_regenerate_ball", "regenerate this ball's commentary");
  const doRegenerate = async (ballId) => {
    setRegenerating(ballId);
    try {
      const r = await api.post(`/api/scoring/balls/${ballId}/commentary/regenerate/`);
      setRows((prev) => prev.map((b) => b.id === ballId ? { ...b, commentary: r.data.commentary } : b));
    } finally {
      setRegenerating(null);
    }
  };
  const regenerate = (ballId) => commentaryConfirm.confirm(() => doRegenerate(ballId));
  // over_number is 0-based on ingested matches and 1-based on
  // live-scored ones, so read the innings' first over off the balls
  // rather than assuming. See deriveOverBase.
  const overBase = deriveOverBase(rows);
  if (loading) return null;
  if (rows.length === 0) return null;
  return (
    <Section title={`Commentary (${rows.length} balls)`}>
      <div className="space-y-1.5 max-h-[60vh] overflow-y-auto pr-2">
        {rows.map((b) => (
          <div
            key={b.id}
            className={`flex items-start gap-2 py-1.5 px-2 rounded text-sm ${
              b.has_wicket ? "bg-red-500/10 border-l-2 border-red-500" :
              b.runs_off_bat === 6 ? "bg-purple-500/10 border-l-2 border-purple-500" :
              b.runs_off_bat === 4 ? "bg-emerald-500/10 border-l-2 border-emerald-500" :
              "hover:bg-bg-secondary/40"
            }`}
          >
            <span className="font-mono text-xs text-text-secondary min-w-[3.5rem]">
              {(Number(b.over_number) || 0) - overBase}.{b.ball_in_over}{!b.is_legal && "*"}
            </span>
            <span className="flex-1 text-text-primary">
              {b.commentary || <em className="text-text-secondary">No commentary</em>}
            </span>
            <button
              onClick={() => regenerate(b.id)}
              disabled={regenerating === b.id}
              className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded text-text-secondary hover:text-text-primary disabled:opacity-50"
              title="Rewrite this ball's commentary via AI — 3 Zencredits."
            >
              {regenerating === b.id ? "…" : "✨ AI · 3 ZC"}
            </button>
          </div>
        ))}
      </div>
      <CreditConfirmModal {...commentaryConfirm.modalProps} />
    </Section>
  );
}

function MatchEventsSection({ matchId }) {
  const [data, setData] = useState({ subs: [], warnings: [], penalties: [], conduct: [] });
  useEffect(() => {
    let alive = true;
    Promise.all([
      api.get(`/api/matches/${matchId}/substitutions/`).then(r => r.data?.results || []).catch(() => []),
      api.get(`/api/matches/${matchId}/warnings/`).then(r => r.data?.results || []).catch(() => []),
      api.get(`/api/matches/${matchId}/penalties/`).then(r => r.data?.results || []).catch(() => []),
      api.get(`/api/matches/${matchId}/conduct/`).then(r => r.data?.results || []).catch(() => []),
    ]).then(([subs, warnings, penalties, conduct]) => alive && setData({ subs, warnings, penalties, conduct }));
    return () => { alive = false; };
  }, [matchId]);
  const total = data.subs.length + data.warnings.length + data.penalties.length + data.conduct.length;
  if (total === 0) return null;
  return (
    <Section title={`Match events (${total})`}>
      <div className="space-y-2 text-sm">
        {data.subs.map((s) => (
          <div key={s.id} className="flex items-start gap-2">
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-sky-500/15 text-sky-400">{s.type}</span>
            <span className="text-text-primary">
              {s.player_out_name} → {s.player_in_name}
              {s.reason && <span className="text-text-secondary italic ml-1">({s.reason})</span>}
            </span>
          </div>
        ))}
        {data.warnings.map((w) => (
          <div key={w.id} className="flex items-start gap-2">
            <span className={`text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded ${w.level === "FINAL" ? "bg-red-500/15 text-red-400" : "bg-amber-500/15 text-amber-400"}`}>
              {w.level} WARN
            </span>
            <span className="text-text-primary">{w.violation.replaceAll("_", " ")}{w.note && <span className="text-text-secondary italic ml-1">— {w.note}</span>}</span>
          </div>
        ))}
        {data.penalties.map((p) => (
          <div key={p.id} className="flex items-start gap-2">
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-button-primary/15 text-button-primary">+{p.runs}</span>
            <span className="text-text-primary">{p.reason.replaceAll("_", " ")} → {p.awarded_to}</span>
          </div>
        ))}
        {data.conduct.map((c) => (
          <div key={c.id} className="flex items-start gap-2">
            <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded bg-purple-500/15 text-purple-400">{c.level}</span>
            <span className="text-text-primary">{c.player_name}: {c.offence} ({c.demerit_points} dp)</span>
          </div>
        ))}
      </div>
    </Section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

function Shell({ children }) { return <div className="p-3 sm:p-6 max-w-5xl mx-auto">{children}</div>; }
function Section({ title, icon, right, children }) {
  return (
    <div className="bg-bg-primary border border-border-default rounded-2xl p-4 sm:p-6 mb-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-base font-bold flex items-center gap-2">{icon}{title}</h2>
        {right}
      </div>
      {children}
    </div>
  );
}

// 2026-06-09 — single row in the Match notes feed. Severity drives
// the left-border colour: alert (rose) for wickets / rain / injuries,
// watch (amber) for DRS / penalties / free-hits, info (slate) for
// everything else. Timestamp renders in the operator's local tz.
function MatchNoteRow({ note }) {
  const severityClass =
    note.severity === "alert"
      ? "border-l-rose-400/60 bg-rose-500/5"
      : note.severity === "watch"
      ? "border-l-amber-400/60 bg-amber-500/5"
      : "border-l-border-default bg-bg-secondary/30";
  let timeLabel = "";
  if (note.timestamp) {
    try {
      const d = new Date(note.timestamp);
      timeLabel = d.toLocaleTimeString(undefined, {
        hour: "2-digit", minute: "2-digit",
      });
    } catch { /* ignore */ }
  }
  return (
    <li className={`flex items-start gap-3 border-l-2 ${severityClass} rounded-r px-3 py-2`}>
      <div className="flex flex-col items-end shrink-0 min-w-[64px]">
        {timeLabel && (
          <span className="text-[10px] text-text-secondary font-mono">{timeLabel}</span>
        )}
        {note.over_ball && (
          <span className="text-[10px] text-text-primary font-mono font-semibold">{note.over_ball}</span>
        )}
      </div>
      <div className="flex-1 text-sm text-text-primary">{note.text}</div>
    </li>
  );
}
function ResultBanner({ result, resultType }) {
  // resultType is the persisted Match.result_type (NEW 2026-05-02): one of
  // DECISIVE / TIE / NO_RESULT / ABANDONED / DRAW. Use it for outcomes the
  // legacy `result` object doesn't model.
  const pill = (text, color) => (
    <span className={`text-[10px] uppercase tracking-wider rounded-full px-2.5 py-1 border ${color}`}>{text}</span>
  );
  if (resultType === "NO_RESULT") return pill("No result", "text-amber-300 border-amber-400/40 bg-amber-500/15");
  if (resultType === "ABANDONED") return pill("Abandoned",  "text-rose-300 border-rose-400/40 bg-rose-500/15");
  if (resultType === "TIE")        return pill("Tied (no super over)", "text-yellow-300 border-yellow-400/40 bg-yellow-500/15");
  if (resultType === "DRAW" || result?.draw) return pill("Draw", "text-blue-300 border-blue-400/40 bg-blue-500/15");
  // 2026-06-05 — show "Result pending" ONLY when the match isn't yet
  // decided. If the operator marked the match DECISIVE but the backend
  // didn't manage to compute a winner (e.g. mid-over chase end), surface
  // a "Match completed" pill instead of the confusing "pending" label.
  if (!result) {
    if (resultType === "DECISIVE") {
      return pill("Match completed", "text-emerald-300 border-emerald-400/40 bg-emerald-500/15");
    }
    return pill("Result pending", "text-text-secondary border-border-default");
  }
  return (
    <div className="text-right">
      <div className="text-[10px] uppercase tracking-[0.18em] text-text-secondary">Winner</div>
      <div className="text-button-primary font-bold text-lg">{result.winner_name}</div>
      {/* margin_text wins over the by_* fields when set — used for
          D/L, AWARDED, and other engine-override outcomes from the
          ingested scorecards (M11 'GM won by 10 Wkts by D/L method'). */}
      {result.margin_text && (
        <div className="text-xs text-text-secondary">{result.margin_text}</div>
      )}
      {!result.margin_text && typeof result.by_runs === "number" && <div className="text-xs text-text-secondary">won by {result.by_runs} run{result.by_runs === 1 ? "" : "s"}</div>}
      {!result.margin_text && typeof result.by_wickets === "number" && <div className="text-xs text-text-secondary">won by {result.by_wickets} wicket{result.by_wickets === 1 ? "" : "s"}</div>}
      {!result.margin_text && result.by_innings && <div className="text-xs text-text-secondary">won by an innings</div>}
      {/* 2026-06-06 (v3) — Super Over result line. Backend populates
          by_super_over + super_over_margin_runs (run margin within
          the decisive pair) + super_over_pair_number (1, 2, 3, …)
          when an SO decided the match. Pair number is surfaced so
          multi-SO matches read "won … via Super Over 2" instead of
          ambiguously "via Super Over". */}
      {result.by_super_over && (
        <div className="text-xs text-amber-300">
          {typeof result.super_over_margin_runs === "number"
            ? `won by ${result.super_over_margin_runs} run${result.super_over_margin_runs === 1 ? "" : "s"} via Super Over`
            : "via Super Over"}
          {result.super_over_pair_number ? ` ${result.super_over_pair_number}` : ""}
        </div>
      )}
    </div>
  );
}

// Partnership ordinal — "1st wkt" / "2nd wkt" / "Unbroken". Shared by
// the server-fed and FOW-derived render paths.
function _partnershipLabel(wkt) {
  if (wkt === "*") return "Unbroken";
  const n = Number(wkt);
  const suffix = n === 1 ? "st" : n === 2 ? "nd" : n === 3 ? "rd" : "th";
  return `${n}${suffix} wkt`;
}

// Per-partnership row used inside MatchPartnershipsSection. `accent`
// lets the column pick a team-coloured bar; the dark + light shades
// mirror the founder reference image's blue / amber split.
function PartnershipRow({ partnership: p, accent }) {
  const b1 = (p.batters && p.batters[0]) || { name: "—", runs: 0, balls: 0 };
  const b2 = (p.batters && p.batters[1]) || { name: "—", runs: 0, balls: 0 };
  const tot = Math.max(0, p.runs || 0);
  const b1r = Math.max(0, b1.runs || 0);
  const b2r = Math.max(0, b2.runs || 0);
  const b1pct = tot > 0 ? Math.min(100, (b1r * 100) / tot) : 0;
  const b2pct = tot > 0 ? Math.min(100, (b2r * 100) / tot) : 0;
  return (
    <div className="px-3 py-3 sm:px-4 sm:py-3.5">
      {/* Top — names + total */}
      <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-baseline">
        <div className="min-w-0 text-text-primary text-sm font-medium truncate">{b1.name}</div>
        <div className="font-mono text-text-primary text-base font-bold tabular-nums whitespace-nowrap">
          {p.runs ?? 0}
          <span className="text-text-secondary font-normal text-xs ml-1">({p.balls ?? 0})</span>
          {p.current && <span className="ml-1.5 align-middle text-[9px] uppercase tracking-wider text-emerald-300 border border-emerald-400/40 rounded-full px-1.5 py-0.5">live</span>}
        </div>
        <div className="min-w-0 text-text-primary text-sm font-medium truncate text-right">{b2.name}</div>
      </div>
      {/* Per-batter line + bar */}
      <div className="grid grid-cols-[1fr_auto_1fr] gap-2 items-center mt-1.5">
        <div className="font-mono text-[11px] text-text-secondary tabular-nums">
          {b1r} ({b1.balls ?? 0})
        </div>
        <div className="flex items-center justify-center min-w-[90px] sm:min-w-[130px]">
          <div className="flex h-1.5 w-full rounded-full overflow-hidden bg-bg-secondary/80">
            {b1pct > 0 && (
              <div className={accent.solid} style={{ width: `${b1pct}%` }} title={`${b1.name}: ${b1r} (${b1.balls ?? 0})`} />
            )}
            {b2pct > 0 && (
              <div className={accent.light} style={{ width: `${b2pct}%` }} title={`${b2.name}: ${b2r} (${b2.balls ?? 0})`} />
            )}
          </div>
        </div>
        <div className="font-mono text-[11px] text-text-secondary tabular-nums text-right">
          {b2r} ({b2.balls ?? 0})
        </div>
      </div>
      {/* Meta row — wkt ordinal + ended-by, condensed footer */}
      {!p.current && (
        <div className="mt-1.5 flex items-center justify-between text-[10px] text-text-secondary tabular-nums">
          <span>{_partnershipLabel(p.wkt)}{p.extras > 0 && <span className="ml-1.5 opacity-70">· {p.extras} extras</span>}</span>
          <span>{p.ended_at} ov{p.ended_by ? <span className="ml-1.5 opacity-70">· {p.ended_by}</span> : null}</span>
        </div>
      )}
    </div>
  );
}

// Match-wide partnerships view — two team columns side-by-side, each
// listing that team's partnerships from their batting innings. Matches
// the founder reference image (partnerships.jpeg). For Test matches
// with 4 innings, all innings render in order, two columns per row.
function MatchPartnershipsSection({ innings, partnershipsByInnings }) {
  // Innings that have any partnership data + a batting team name.
  const renderable = (innings || []).filter(
    (inn) => (partnershipsByInnings[inn.innings_number] || []).length > 0
  );
  if (renderable.length === 0) return null;
  // Two accent palettes alternate between columns, matching the
  // image's blue (innings 1) / amber (innings 2) split. After 2
  // innings, cycle back. Tailwind classes are explicit (no template
  // strings) so the JIT compiler keeps them in the build.
  const accents = [
    { solid: "bg-blue-500",   light: "bg-blue-400/50",   header: "text-blue-300" },
    { solid: "bg-amber-500",  light: "bg-amber-400/50",  header: "text-amber-300" },
  ];
  return (
    <Section title="Partnerships">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
        {renderable.map((inn, i) => {
          const accent = accents[i % accents.length];
          const list = partnershipsByInnings[inn.innings_number] || [];
          return (
            <div key={inn.innings_number} className="min-w-0">
              {/* Team header — bold team name + innings label */}
              <div className="flex items-baseline justify-between mb-2">
                <h3 className={`text-base sm:text-lg font-bold truncate ${accent.header}`}>
                  {inn.batting_team_name}
                </h3>
                <span className="text-[10px] uppercase tracking-wider text-text-secondary">
                  {inn.is_super_over
                    ? `Super Over ${Math.ceil((inn.super_over_index || 1) / 2)}`
                    : `Innings ${inn.innings_number}`}
                  <span className="ml-2 opacity-70">{list.length} stand{list.length === 1 ? "" : "s"}</span>
                </span>
              </div>
              <div className="rounded-lg border border-border-default bg-bg-secondary/30 divide-y divide-border-default/60">
                {list.map((p, j) => (
                  <PartnershipRow key={j} partnership={p} accent={accent} />
                ))}
              </div>
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function InningsBlock({ inn, matchType, playingXI, partnerships: serverPartnerships }) {
  const agg = inn.aggregate;
  // Did Not Bat — XI players who never came to the crease this innings.
  const battingXI = playingXI?.[inn.batting_team_id] || [];
  const battedNames = new Set((inn.batting_card || []).map((b) => b.player_name));
  const dnb = battingXI.filter((p) => !battedNames.has(p.player_name));
  // Fall of wickets — backend ships [wkt#, runs, "over.ball", batter_name] tuples.
  const fow = inn.fow || [];
  // Prefer the server-computed partnerships (richer — balls + per-batter
  // contributions). Fall back to FOW-derived computation if the endpoint
  // failed so the page never goes blank on the partnerships panel.
  const partnerships = (() => {
    if (Array.isArray(serverPartnerships) && serverPartnerships.length > 0) {
      return serverPartnerships.map((p) => ({
        ...p,
        // Normalise: backend sends ended_by_name; legacy code used ended_by.
        ended_by: p.ended_by_name || p.ended_by || "",
      }));
    }
    // Legacy fallback — derive from FOW. Misses balls + per-batter splits
    // but the basic per-wicket runs are correct.
    const out = [];
    let prevRuns = 0;
    for (const row of fow) {
      if (!Array.isArray(row) || row.length < 4) continue;
      const [, runs] = row;
      out.push({
        wkt: out.length + 1,
        runs: runs - prevRuns,
        ended_at: row[2],
        ended_by: row[3],
      });
      prevRuns = runs;
    }
    if (agg && (agg.runs ?? 0) > prevRuns) {
      out.push({
        wkt: "*",
        runs: (agg.runs ?? 0) - prevRuns,
        current: true,
      });
    }
    return out;
  })();
  // Bowler totals (footer row).
  const bowlTot = (inn.bowling_card || []).reduce(
    (acc, b) => ({
      balls: acc.balls + (b.balls || 0),
      maidens: acc.maidens + (b.maidens || 0),
      runs_conceded: acc.runs_conceded + (b.runs_conceded || 0),
      wickets: acc.wickets + (b.wickets || 0),
    }),
    { balls: 0, maidens: 0, runs_conceded: 0, wickets: 0 }
  );
  const totalOvers = formatBowlerBalls(matchType, bowlTot.balls);
  // Innings header label. For Super Over innings, render as
  // "Super Over N — <team>" using the SO pair number (super_over_index
  // 1+2 = pair 1, 3+4 = pair 2, ...). Otherwise the regular "1st / 2nd
  // / Nth Innings" labelling.
  const innTitle = inn.is_super_over
    ? `Super Over ${Math.ceil((inn.super_over_index || 1) / 2)} — ${inn.batting_team_name}`
    : `${inn.innings_number === 1 ? "1st" : inn.innings_number === 2 ? "2nd" : `${inn.innings_number}th`} Innings — ${inn.batting_team_name}`;
  return (
    <Section
      title={innTitle}
      right={agg && (
        <span className="font-mono text-base">
          <span className="font-bold text-text-primary">{agg.runs ?? 0}</span>
          {/* 2026-06-05 — "all out" badge replaces the "/N wickets" suffix
              when agg.is_all_out is True. Cricinfo/Cricbuzz convention:
              "350 all out", not "350/9". Backend emits is_all_out when
              effective_wickets (real dismissals + retired-hurt-no-return)
              hits 10 (regular) or 2 (Super Over). */}
          {agg.is_all_out ? (
            <span className="ml-1.5 text-[10px] uppercase tracking-wider font-bold text-rose-300 border border-rose-400/40 rounded-full px-2 py-0.5">all out</span>
          ) : (
            <>
              <span className="text-text-secondary">/</span>
              <span className="font-bold text-text-primary">{agg.wickets ?? 0}</span>
            </>
          )}
          <span className="text-text-secondary text-sm ml-1">({formatInningsProgress(matchType, { legalBalls: agg.legal_balls ?? 0, completedOvers: agg.completed_overs ?? 0 })})</span>
          {agg.is_chasing && (agg.target_runs || agg.revised_target_runs) && (() => {
            // "Target" = runs to win, per cricket convention. DB stores
            // `target_runs` as opponent's raw first-innings total; the
            // score-to-beat is total+1. DLS values are stored as the
            // exact score-to-reach per regulation, so no +1 there.
            // max_overs surfaces the shortened innings length so the
            // scorer sees both halves of a DLS revision in one glance.
            const isDLS = agg.revised_target_runs != null;
            const targetToWin = isDLS
              ? agg.revised_target_runs
              : agg.target_runs + 1;
            return (
              <span className="ml-3 text-[11px] uppercase tracking-wider text-amber-300">
                Target {targetToWin}
                {agg.max_overs ? <span className="text-text-secondary"> · {formatMaxLength(matchType, agg.max_overs)}</span> : null}
                {isDLS && <span className="ml-1 text-amber-400 border border-amber-400/40 rounded px-1">DLS</span>}
              </span>
            );
          })()}
        </span>
      )}
    >
      <div className="overflow-x-auto rounded-lg border border-border-default mb-4">
        <table className="w-full text-sm">
          <thead className="bg-table-header text-text-secondary text-[10px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Batter</th>
              <th className="px-3 py-2 text-right">R</th>
              <th className="px-3 py-2 text-right">B</th>
              <th className="px-3 py-2 text-right">4s</th>
              <th className="px-3 py-2 text-right">6s</th>
              <th className="px-3 py-2 text-right">SR</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {(inn.batting_card || []).map((b) => {
              // 2026-06-10 founder ask — retired-hurt batters get a
              // dedicated amber star so they're visually distinct from
              // the not-out (green-star) and dismissed (no-star)
              // categories. ICC scorecards mark retired-hurt rows with
              // a "*" suffix; mirror that semantic with an amber star
              // glyph keyed off the dismissal_text the backend emits.
              const dismText = (b.dismissal_text || "").toLowerCase();
              const isRetiredHurt = dismText.includes("retired");
              const isNotOut = !b.is_out && !dismText;
              return (
              <tr key={b.player_id} className="border-t border-border-default">
                <td className="px-3 py-2.5">
                  <div>
                    <SubArrow role={b.substitution_role} />
                    {b.player_name}
                    {isNotOut && <Star className="inline w-3 ml-1 text-emerald-400" />}
                    {isRetiredHurt && (
                      <Star
                        className="inline w-3 ml-1 text-amber-400 fill-amber-400/30"
                        aria-label="Retired hurt"
                      />
                    )}
                  </div>
                  {/* 2026-06-05 — render dismissal_text whenever it's
                      non-empty (not just when is_out). RETIRED_HURT /
                      RETIRED_NOT_OUT batters have a BallWicket row +
                      dismissal_text="retired hurt" but is_out=False
                      (since they're not officially dismissed for wicket
                      count). Without this gate they previously rendered
                      as "N.O." — three "not out" batters at once. */}
                  {(b.is_out || b.dismissal_text) && (
                    <div className="text-[11px] text-text-secondary italic mt-0.5">
                      {b.dismissal_text || "out"}
                    </div>
                  )}
                </td>
                <td className="px-3 py-2.5 text-right font-mono text-text-primary font-semibold">{b.runs}</td>
                <td className="px-3 py-2.5 text-right font-mono text-text-secondary">{b.balls}</td>
                <td className="px-3 py-2.5 text-right font-mono text-text-secondary">{b.fours}</td>
                <td className="px-3 py-2.5 text-right font-mono text-text-secondary">{b.sixes}</td>
                <td className="px-3 py-2.5 text-right font-mono text-text-secondary">{b.strike_rate ?? "—"}</td>
                <td className="px-3 py-2.5 text-right">{(!b.is_out && !b.dismissal_text) && <span className="text-[10px] uppercase tracking-wider text-emerald-400 border border-emerald-400/40 rounded-full px-1.5 py-0.5">N.O.</span>}</td>
              </tr>
              );
            })}
            {(!inn.batting_card || inn.batting_card.length === 0) && <tr><td colSpan={7} className="px-3 py-6 text-center text-text-secondary">No batting card.</td></tr>}
          </tbody>
          <tfoot>
            {agg && <tr className="border-t border-border-default bg-bg-secondary/40">
              <td className="px-3 py-2 text-text-secondary text-xs">Extras: <span className="text-text-primary font-semibold">{agg.extras ?? 0}</span> · Boundaries: <span className="text-text-primary font-semibold">{agg.boundary_count ?? 0}</span> · Total <span className="text-text-primary font-bold">{agg.runs ?? 0}{agg.is_all_out ? (<span className="ml-1 text-rose-300">all out</span>) : (<>/{agg.wickets ?? 0}</>)}</span> in {formatInningsProgress(matchType, { legalBalls: agg.legal_balls ?? 0, completedOvers: agg.completed_overs ?? 0 })}{isHundred(matchType) ? "" : " ov"}</td>
              <td colSpan={6}></td>
            </tr>}
          </tfoot>
        </table>
      </div>

      <div className="overflow-x-auto rounded-lg border border-border-default">
        <table className="w-full text-sm">
          <thead className="bg-table-header text-text-secondary text-[10px] uppercase tracking-wider">
            <tr>
              <th className="px-3 py-2 text-left">Bowler ({inn.bowling_team_name})</th>
              <th className="px-3 py-2 text-right">{bowlerBallsColumnLabel(matchType)}</th>
              <th className="px-3 py-2 text-right">M</th>
              <th className="px-3 py-2 text-right">R</th>
              <th className="px-3 py-2 text-right">W</th>
              <th className="px-3 py-2 text-right">Econ</th>
            </tr>
          </thead>
          <tbody>
            {(inn.bowling_card || []).map((b) => (
              <tr key={b.player_id} className="border-t border-border-default">
                <td className="px-3 py-2.5"><SubArrow role={b.substitution_role} />{b.player_name}</td>
                <td className="px-3 py-2.5 text-right font-mono text-text-secondary">{b.balls != null ? formatBowlerBalls(matchType, b.balls) : (b.overs ?? "—")}</td>
                <td className="px-3 py-2.5 text-right font-mono text-text-secondary">{b.maidens ?? 0}</td>
                <td className="px-3 py-2.5 text-right font-mono">{b.runs_conceded}</td>
                <td className="px-3 py-2.5 text-right font-mono text-text-primary font-semibold">{b.wickets}</td>
                <td className="px-3 py-2.5 text-right font-mono text-text-secondary">{b.economy ?? "—"}</td>
              </tr>
            ))}
            {(!inn.bowling_card || inn.bowling_card.length === 0) && <tr><td colSpan={6} className="px-3 py-6 text-center text-text-secondary">No bowling card.</td></tr>}
          </tbody>
          {(inn.bowling_card || []).length > 0 && (
            <tfoot>
              <tr className="border-t border-border-default bg-bg-secondary/40 font-mono text-text-secondary text-xs">
                <td className="px-3 py-2 text-right uppercase tracking-wider text-[10px]">Total</td>
                <td className="px-3 py-2 text-right">{totalOvers}</td>
                <td className="px-3 py-2 text-right">{bowlTot.maidens}</td>
                <td className="px-3 py-2 text-right">{bowlTot.runs_conceded}</td>
                <td className="px-3 py-2 text-right text-text-primary font-semibold">{bowlTot.wickets}</td>
                <td className="px-3 py-2"></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      {/* Did not bat — chips listing XI players who never came to the crease */}
      {dnb.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-text-secondary mb-1.5">Did not bat</div>
          <div className="flex flex-wrap gap-1.5">
            {dnb.map((p) => (
              <span key={p.player_id} className="px-2 py-0.5 text-xs rounded bg-bg-secondary border border-border-default text-text-primary">
                {p.player_name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Fall of wickets — cricinfo-style chip row.
          2026-06-05 — backend now emits a 5th element `is_retired_hurt`
          for retired-hurt entries. Those chips render with a "*" prefix
          and no wkt count (retiring isn't a wicket; running total is
          still useful context for when the batter left the field). */}
      {fow.length > 0 && (
        <div className="mt-4">
          <div className="text-[10px] uppercase tracking-[0.18em] text-text-secondary mb-1.5">Fall of wickets</div>
          <div className="flex flex-wrap gap-1.5 text-xs">
            {fow.map((row, idx) => {
              if (!Array.isArray(row) || row.length < 4) return null;
              const [wkt, runs, over, batter, isRetiredHurt] = row;
              if (isRetiredHurt) {
                return (
                  <span
                    key={idx}
                    className="px-2 py-0.5 rounded bg-amber-500/10 border border-amber-400/30 text-amber-200"
                    title="Retired hurt — not a wicket, but the running total at the moment they left the field"
                  >
                    <span className="font-mono text-amber-300">* {runs}</span>
                    <span className="opacity-60"> · </span>
                    <span>{batter}</span>
                    <span className="opacity-60"> · </span>
                    <span className="font-mono">{over} ov</span>
                    <span className="ml-1 text-[9px] uppercase tracking-wider opacity-70">retired</span>
                  </span>
                );
              }
              return (
                <span key={idx} className="px-2 py-0.5 rounded bg-bg-secondary border border-border-default text-text-secondary">
                  <span className="font-mono text-text-primary">{wkt}-{runs}</span>
                  <span className="opacity-60"> · </span>
                  <span>{batter}</span>
                  <span className="opacity-60"> · </span>
                  <span className="font-mono">{over} ov</span>
                </span>
              );
            })}
          </div>
        </div>
      )}

      {/* Partnerships — 2026-06-05 (v2) — per-innings block hidden in
          favour of the match-wide MatchPartnershipsSection rendered at
          the scorecard root. The per-innings block is preserved
          underneath the InningsBlock guards (see `false &&`) so the
          row layout logic stays in one place for reuse by the new
          shared component below. */}
      {false && partnerships.length > 0 && (
        <div className="mt-4">
          <div className="flex items-center justify-between mb-2">
            <div className="text-[10px] uppercase tracking-[0.18em] text-text-secondary">Partnerships · {inn.batting_team_name}</div>
            <div className="text-[10px] uppercase tracking-wider text-text-secondary">{partnerships.length} stand{partnerships.length === 1 ? "" : "s"}</div>
          </div>
          <div className="rounded-lg border border-border-default bg-bg-secondary/30 divide-y divide-border-default/60">
            {partnerships.map((p, i) => {
              const b1 = (p.batters && p.batters[0]) || { name: "—", runs: 0, balls: 0 };
              const b2 = (p.batters && p.batters[1]) || { name: "—", runs: 0, balls: 0 };
              // Per-batter share of the partnership runs. Extras +
              // overthrow share collapses into the gap between the two
              // segments so the bar's filled width = b1.runs + b2.runs.
              const tot = Math.max(0, p.runs || 0);
              const b1r = Math.max(0, b1.runs || 0);
              const b2r = Math.max(0, b2.runs || 0);
              const b1pct = tot > 0 ? Math.min(100, (b1r * 100) / tot) : 0;
              const b2pct = tot > 0 ? Math.min(100, (b2r * 100) / tot) : 0;
              return (
                <div key={i} className="px-3 py-3 sm:px-4 sm:py-3.5">
                  {/* Top row — names + total */}
                  <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-baseline">
                    <div className="min-w-0 text-text-primary text-sm font-medium truncate">{b1.name}</div>
                    <div className="font-mono text-text-primary text-base font-bold tabular-nums whitespace-nowrap">
                      {p.runs ?? 0}
                      <span className="text-text-secondary font-normal text-xs ml-1">({p.balls ?? 0})</span>
                      {p.current && <span className="ml-1.5 align-middle text-[9px] uppercase tracking-wider text-emerald-300 border border-emerald-400/40 rounded-full px-1.5 py-0.5">live</span>}
                    </div>
                    <div className="min-w-0 text-text-primary text-sm font-medium truncate text-right">{b2.name}</div>
                  </div>

                  {/* Bar row — left-aligned per-batter line under each
                      name, segmented bar in the middle. Bar widths come
                      from b1/b2 share of partnership runs; the gap is
                      the extras share (so b1 + b2 + extras = total). */}
                  <div className="grid grid-cols-[1fr_auto_1fr] gap-3 items-center mt-1.5">
                    <div className="font-mono text-[11px] text-text-secondary tabular-nums">
                      {b1r} ({b1.balls ?? 0})
                    </div>
                    <div className="flex items-center justify-center min-w-[120px] sm:min-w-[180px]">
                      <div className="flex h-1.5 w-full rounded-full overflow-hidden bg-bg-secondary/80">
                        {b1pct > 0 && (
                          <div className="bg-button-primary" style={{ width: `${b1pct}%` }} title={`${b1.name}: ${b1r} (${b1.balls ?? 0})`} />
                        )}
                        {b2pct > 0 && (
                          <div className="bg-button-primary/45" style={{ width: `${b2pct}%` }} title={`${b2.name}: ${b2r} (${b2.balls ?? 0})`} />
                        )}
                      </div>
                    </div>
                    <div className="font-mono text-[11px] text-text-secondary tabular-nums text-right">
                      {b2r} ({b2.balls ?? 0})
                    </div>
                  </div>

                  {/* Meta — partnership ordinal + ended-by, condensed
                      so it doesn't compete with the contribution-share
                      visual above. Skipped for the live partnership. */}
                  {!p.current && (
                    <div className="mt-1.5 flex items-center justify-between text-[10px] text-text-secondary tabular-nums">
                      <span>{_partnershipLabel(p.wkt)}{p.extras > 0 && <span className="ml-1.5 opacity-70">· {p.extras} extras</span>}</span>
                      <span>{p.ended_at} ov{p.ended_by ? <span className="ml-1.5 opacity-70">· {p.ended_by}</span> : null}</span>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </Section>
  );
}

function AIReportSection({ matchId }) {
  const [report, setReport] = useState("");
  const [cached, setCached] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getMatchAIReport(matchId)
      .then((res) => { if (alive) { setReport(res.data?.report || ""); setCached(!!res.data?.cached); } })
      .catch(() => {})
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [matchId]);

  // Wrap the generate call with a "this will cost N credits" confirmation.
  // The hook fetches the cost preview, opens the modal, then runs the
  // pending action on confirm. The 402 axios interceptor handles
  // insufficient_credits cases globally.
  const aiReportConfirm = useCreditConfirm("match_summary_report", "AI match report");

  const doGenerate = async () => {
    setGenerating(true); setErr("");
    try {
      const res = await generateMatchAIReport(matchId);
      setReport(res.data?.report || "");
      setCached(false);
    } catch (e) {
      // Don't show 402 inline — the global modal already opened.
      if (e?.response?.status !== 402) {
        setErr(
          e?.response?.data?.detail
            || e?.message
            || "Generation failed."
        );
      }
    } finally {
      setGenerating(false);
    }
  };

  const onGenerate = () => aiReportConfirm.confirm(doGenerate);

  return (
    <Section
      title="AI Match Summary · Free"
      icon={<Sparkles className="w-4 text-button-primary" />}
      right={
        report && cached && (
          <button
            onClick={onGenerate}
            disabled={generating}
            className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-text-secondary hover:text-text-primary border border-border-default hover:border-button-primary/40 rounded-full px-2.5 py-1 transition-colors disabled:opacity-40"
            title="Regenerate the narrative with the latest data"
          >
            <RotateCw className={`w-3 ${generating ? "animate-spin" : ""}`} />
            Regenerate
          </button>
        )
      }
    >
      {loading ? (
        <p className="text-xs text-text-secondary">Loading…</p>
      ) : report ? (
        <p className="text-sm leading-relaxed whitespace-pre-line text-text-primary">{report}</p>
      ) : (
        <div>
          <p className="text-xs text-text-secondary mb-3">
            A 5-7 sentence narrative summary using the toss, scorecard, top performers and FoW for this match. Powered by Claude.
            <span className="block mt-1 text-emerald-300">Free · no Zencredits.</span>
          </p>
          <button
            onClick={onGenerate}
            disabled={generating}
            title="Generate AI match summary — free"
            className="bg-button-primary hover:brightness-110 text-white text-xs font-semibold px-4 py-2 rounded-md flex items-center gap-2 disabled:opacity-50 transition-all"
          >
            <Sparkles className="w-3.5" />
            {generating ? "Generating…" : "Generate summary · Free"}
          </button>
        </div>
      )}
      {err && (
        <div className="mt-3 bg-button-primary/10 border border-button-primary/30 rounded-md px-3 py-2 text-xs text-button-primary">
          {err}
          {err.toLowerCase().includes("anthropic_api_key") && (
            <span className="block mt-1 text-text-secondary">
              Add <span className="font-mono">ANTHROPIC_API_KEY</span> to <span className="font-mono">apps/.env</span> on the server and restart gunicorn.
            </span>
          )}
        </div>
      )}
      <CreditConfirmModal {...aiReportConfirm.modalProps} />
    </Section>
  );
}

function FowSection({ innings }) {
  const haveAny = innings.some((i) => (i.fow || []).length > 0);
  if (!haveAny) {
    return (
      <Section title="Fall of wickets" right={<span className="text-[10px] text-amber-300 uppercase tracking-wider">No source data</span>}>
        <p className="text-xs text-text-secondary">No FOW recorded for this fixture.</p>
      </Section>
    );
  }
  return (
    <Section title="Fall of wickets">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {innings.map((inn) => {
          const fow = inn.fow || [];
          return (
            <div key={`fow-${inn.innings_number}`} className="bg-bg-secondary/40 border border-border-default rounded-lg p-4">
              <div className="font-semibold text-sm mb-2">
                {inn.batting_team_name}
                <span className="text-[10px] uppercase tracking-wider text-text-secondary ml-2">
                  {inn.is_super_over
                    ? `Super Over ${Math.ceil((inn.super_over_index || 1) / 2)}`
                    : `Innings ${inn.innings_number}`}
                </span>
              </div>
              {fow.length === 0 ? (
                <p className="text-xs text-text-secondary">No FOW data.</p>
              ) : (
                <table className="w-full text-xs">
                  <thead>
                    <tr className="text-text-secondary uppercase tracking-wider text-[10px]">
                      <th className="text-left py-1">Wkt</th>
                      <th className="text-left py-1">Batter</th>
                      <th className="text-right py-1">Score</th>
                      <th className="text-right py-1">Over</th>
                    </tr>
                  </thead>
                  <tbody>
                    {fow.map((row, idx) => (
                      <tr key={idx} className="border-t border-border-default/60">
                        <td className="py-1 font-mono text-button-primary font-semibold">{row[0]}</td>
                        <td className="py-1">{row[3] || "—"}</td>
                        <td className="py-1 text-right font-mono">{row[1]}</td>
                        <td className="py-1 text-right font-mono text-text-secondary">{row[2] || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function XIList({ team, rows }) {
  // Tap-to-edit: dispatch the same window CustomEvent that the kebab in
  // BallLayout uses. If BallByBallPage is mounted (scorer is in the
  // scoring console), it opens EditPlayingXIModal preselected on the
  // tapped player. On the standalone Scorecard route there is no
  // listener and the click is a harmless no-op.
  const openEditor = (playerId) => {
    window.dispatchEvent(
      new CustomEvent("edit-playing-xi:open", { detail: { playerId } }),
    );
  };
  return (
    <div className="bg-bg-secondary/40 border border-border-default rounded-lg p-4">
      <div className="font-semibold mb-2">{team?.name}</div>
      <ul className="space-y-1.5 text-sm">
        {rows.length === 0 && <li className="text-text-secondary text-xs">No XI on file.</li>}
        {rows.map((p) => {
          const isOut = p.substitution_role === "impact_out" || p.substitution_role === "concussion_out";
          return (
            <li key={p.player_id} className="border-b border-border-default/60 pb-1 last:border-0">
              <button
                type="button"
                onClick={() => openEditor(p.player_id)}
                title="Edit XI — fix scorer mistake or change details"
                className="w-full flex items-center justify-between text-left rounded px-1 -mx-1 hover:bg-bg-secondary/70 focus:outline-none focus:ring-1 focus:ring-button-primary/40"
              >
                <span className={isOut ? "opacity-70" : ""}>
                  <span className="text-text-secondary text-[11px] mr-2 font-mono">
                    {p.batting_position === 99 ? "—" : `#${p.batting_position}`}
                  </span>
                  <SubArrow role={p.substitution_role} />
                  {p.player_name}
                  {p.is_captain && <span className="ml-1.5 text-[10px] uppercase tracking-wider text-button-primary border border-button-primary/40 rounded-full px-1.5">C</span>}
                  {p.is_wicket_keeper && <span className="ml-1 text-[10px] uppercase tracking-wider text-emerald-300 border border-emerald-400/40 rounded-full px-1.5">WK</span>}
                </span>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SubstitutionsSection({ subs, team1, team2 }) {
  if (!subs || subs.length === 0) return null;
  const teamName = (id) => {
    if (team1?.id === id) return team1.name;
    if (team2?.id === id) return team2.name;
    return "—";
  };
  return (
    <Section title="Substitutions" icon={<ArrowRightLeft className="w-3.5 h-3.5" />}>
      <div className="bg-bg-secondary/40 border border-border-default rounded-lg p-4">
        <ul className="space-y-2 text-sm">
          {subs.map((s) => {
            const isImpact = s.type === "IMPACT";
            return (
              <li key={s.id} className="flex items-center justify-between gap-3 flex-wrap">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-[10px] uppercase tracking-wider border ${
                    isImpact
                      ? "border-blue-500/40 bg-blue-500/15 text-blue-300"
                      : "border-amber-500/40 bg-amber-500/15 text-amber-300"
                  }`}>
                    <ArrowRightLeft className="w-3 h-3" />
                    {isImpact ? "Impact" : "Concussion"}
                  </span>
                  <span className="text-text-secondary text-xs">{teamName(s.team_id)}</span>
                </div>
                <div className="flex items-center gap-2 text-sm">
                  <span className="text-rose-300 line-through">{s.player_out_name}</span>
                  <ArrowDown className="w-3 h-3 text-rose-400" />
                  <span className="text-text-secondary">→</span>
                  <ArrowUp className="w-3 h-3 text-emerald-400" />
                  <span className="text-emerald-300 font-semibold">{s.player_in_name}</span>
                  {s.over_number != null && (
                    <span className="text-text-secondary text-[11px] font-mono ml-2">
                      {/* No ball list here to read the innings' first
                          over from, so treat over_number as overs
                          completed — how every ingested match stores it.
                          The old `(x || 1) - 1` was wrong for those AND
                          collapsed overs 0 and 1 onto the same label. */}
                      @ {Number(s.over_number) || 0}.{s.ball_in_over ?? 0}
                    </span>
                  )}
                </div>
              </li>
            );
          })}
        </ul>
      </div>
    </Section>
  );
}

// Renders a coloured arrow next to a player's name when they came in or
// went out via an Impact / Concussion substitution. Tooltip on hover.
// IMPACT shows blue-ish; CONCUSSION shows amber-ish. Direction = role.
function SubArrow({ role }) {
  if (!role) return null;
  const isIn = role.endsWith("_in");
  const isImpact = role.startsWith("impact");
  const label = isIn
    ? (isImpact ? "Impact In" : "Concussion In")
    : (isImpact ? "Impact Out" : "Concussion Out");
  const Cls = isIn
    ? "inline-flex items-center gap-0.5 mr-1 px-1 py-0.5 rounded text-[9px] uppercase tracking-wider border border-emerald-500/40 bg-emerald-500/15 text-emerald-300 align-middle"
    : "inline-flex items-center gap-0.5 mr-1 px-1 py-0.5 rounded text-[9px] uppercase tracking-wider border border-rose-500/40 bg-rose-500/15 text-rose-300 align-middle";
  const Icon = isIn ? ArrowUp : ArrowDown;
  return (
    <span className={Cls} title={label}>
      <Icon className="w-2.5 h-2.5" />
      {isIn ? "IN" : "OUT"}
    </span>
  );
}

// ── WAGON WHEEL ──────────────────────────────────────────────────────────────

function WagonWheelSection({ spatial, innings }) {
  const ww = spatial?.ww || [];
  const bt = spatial?.bt || [];
  const bh = spatial?.bh || [];
  const [innFilter, setInnFilter] = useState("all");
  const [batter, setBatter] = useState("all");
  const [runType, setRunType] = useState("all");  // dots / 1 / 2 / 3 / 4 / 6 / wickets

  const inningsById = useMemo(() => {
    const m = {};
    (innings || []).forEach((i) => {
      m[i.innings_number] = i;
    });
    return m;
  }, [innings]);

  // Interconnected filter helper — applies every filter EXCEPT the one
  // currently picking options. So the Batter dropdown only lists
  // batters present in the picked innings + run-type, the Run-type
  // count only counts shots within the picked innings + batter, etc.
  const passInnings = (b) =>
    innFilter === "all" || String(b[0]) === String(innFilter);
  const passBatter = (b) =>
    batter === "all" || (bt[b[1]] || "") === batter;
  const passRunType = (b) => {
    if (runType === "all") return true;
    if (runType === "wickets") return b[3] === 1;
    const r = Number(b[2] || 0);
    if (runType === "dots") return r === 0 && b[3] !== 1;
    return r === Number(runType);
  };

  if (!ww.length) {
    return (
      <Section title="Wagon wheel" right={<span className="text-[10px] text-amber-300 uppercase tracking-wider">No spatial source</span>}>
        <p className="text-xs text-text-secondary">No wagon-wheel points in the source for this fixture (commonly the case for older matches and Test 3rd/4th innings).</p>
      </Section>
    );
  }

  // Innings options — always show every innings the source has.
  const innOptions = useMemo(() => {
    const set = new Set(ww.map((b) => b[0]).filter((x) => x != null));
    return ["all", ...Array.from(set).sort()];
  }, [ww]);

  // Batter options — only the batters who actually appear in the
  // currently-picked innings + run-type. Keeps the dropdown from
  // listing batters that the user can't see right now.
  const batterOptions = useMemo(() => {
    const seen = new Set();
    const list = [];
    ww.forEach((b) => {
      if (!passInnings(b)) return;
      if (!passRunType(b)) return;
      const idx = b[1];
      const nm = bt[idx];
      if (nm && !seen.has(nm)) { seen.add(nm); list.push(nm); }
    });
    list.sort();
    return ["all", ...list];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ww, bt, innFilter, runType]);

  // If the current batter pick falls outside the new options (e.g.
  // user changed innings), reset to "all" so we don't render empty.
  useEffect(() => {
    if (batter !== "all" && !batterOptions.includes(batter)) {
      setBatter("all");
    }
  }, [batter, batterOptions]);

  const filtered = useMemo(() =>
    ww.filter((b) => {
      if (!passInnings(b)) return false;
      if (!passBatter(b)) return false;
      if (!passRunType(b)) return false;
      return b[4] != null && b[5] != null;
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  , [ww, bt, innFilter, batter, runType]);

  // If user selected one batter and they are LEFT-handed, mirror x so the
  // view matches a right-hander's perspective (off side on the left).
  const mirrorX = useMemo(() => {
    if (batter === "all") return false;
    const idx = bt.indexOf(batter);
    return idx >= 0 && (bh[idx] || "").toUpperCase() === "L";
  }, [batter, bt, bh]);

  const stats = useMemo(() => {
    let runs = 0, ones = 0, twos = 0, threes = 0, fours = 0, sixes = 0, wkts = 0;
    filtered.forEach((b) => {
      const r = Number(b[2] || 0); runs += r;
      if (r === 1) ones++;
      else if (r === 2) twos++;
      else if (r === 3) threes++;
      else if (r === 4) fours++;
      else if (r === 6) sixes++;
      if (b[3] === 1) wkts++;
    });
    return { balls: filtered.length, runs, ones, twos, threes, fours, sixes, wkts };
  }, [filtered]);

  return (
    <Section title="Wagon wheel" icon={<Filter className="w-4 h-4" />}>
      <div className="flex flex-wrap gap-3 mb-4">
        <FilterSelect label="Innings" value={innFilter} onChange={setInnFilter}
          options={innOptions.map((v) => ({ value: String(v), label: v === "all" ? "All innings" : `Innings ${v}` }))} />
        <FilterSelect label="Batter" value={batter} onChange={setBatter}
          options={batterOptions.map((nm) => {
            if (nm === "all") return { value: "all", label: "All batters" };
            const idx = bt.indexOf(nm);
            const hand = (bh[idx] || "").toUpperCase();
            return { value: nm, label: hand ? `${nm}  (${hand}H)` : nm };
          })} />
        <FilterSelect label="Outcome" value={runType} onChange={setRunType}
          options={[
            { value: "all",     label: "All shots" },
            { value: "dots",    label: "Dots" },
            { value: "1",       label: "1s" },
            { value: "2",       label: "2s" },
            { value: "3",       label: "3s" },
            { value: "4",       label: "4s" },
            { value: "6",       label: "6s" },
            { value: "wickets", label: "Wickets" },
          ]} />
        {mirrorX && (
          <span className="self-end text-[10px] uppercase tracking-wider text-button-primary border border-button-primary/40 bg-button-primary/10 rounded-full px-2 py-1">
            mirrored (left-hander)
          </span>
        )}
        <span
          className="self-end text-[10px] uppercase tracking-wider text-amber-300/80 border border-amber-300/30 bg-amber-500/10 rounded-full px-2 py-1"
          title="Coordinates derived from named fielding position when broadcast coords aren't in source data — direction is approximate, run-distance is exact."
        >
          approx. direction
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_240px] gap-4">
        <WagonWheelSVG points={filtered} mirrorX={mirrorX} />
        <div>
          {/* Innings reconciliation — actual score vs off-bat plotted.
              Surfaces the extras gap so the wagon's "Off-bat" stat
              below isn't read as the team total by mistake. */}
          {innFilter !== "all" && batter === "all" && inningsById[Number(innFilter)] && (
            <InningsReconciliation
              inn={inningsById[Number(innFilter)]}
              offBatPlotted={stats.runs}
            />
          )}
          {/* Top-line: runs off bat + wickets */}
          <div className="grid grid-cols-2 gap-2 text-xs mb-3">
            <Stat v={stats.runs}  l="Off-bat runs" />
            <Stat v={stats.wkts}  l="Wickets" />
          </div>
          {/* Per-run-value rows — separate line each for 1s / 2s / 3s /
              4s / 6s with the swatch + count alongside. Doubles as
              legend since the swatch colour matches the shot lines. */}
          <div className="bg-bg-secondary/40 border border-border-default rounded-lg p-2.5 text-[11px] space-y-1">
            {[
              ["#d4d4d8", "Dots",   stats.balls - stats.ones - stats.twos - stats.threes - stats.fours - stats.sixes - stats.wkts],
              ["#facc15", "1s",     stats.ones],
              ["#facc15", "2s",     stats.twos],
              ["#facc15", "3s",     stats.threes],
              ["#60a5fa", "4s",     stats.fours],
              ["#fb7185", "6s",     stats.sixes],
              ["#ef4444", "Wickets", stats.wkts],
            ].map(([c, l, n]) => (
              <div key={l} className="flex items-center justify-between gap-2 py-0.5">
                <div className="flex items-center gap-2">
                  <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ background: c }} />
                  <span className="text-text-secondary">{l}</span>
                </div>
                <span className="font-mono text-text-primary tabular-nums">{n}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </Section>
  );
}

function WagonWheelSVG({ points, mirrorX }) {
  // Square canvas centred on the ground image. The wagon_wheel.jpg
  // background carries field rings + sector labels + pitch + stumps
  // — we only overlay shots + the boundary circle.
  //
  // 2026-06-25 — when mirrorX is true (left-hander selected), we
  // horizontally mirror the entire SVG via CSS so Third Man / Fine Leg
  // visually swap. Shot coords stay at their raw natural x — the field
  // is what flips, not the data. JPG text reads reversed in mirrored
  // mode but the sector POSITIONS are now correctly inverted, which
  // is what the user asked for. A "LHB perspective" badge above the
  // chart tells the operator the orientation has flipped.
  const SIZE = 360;
  const CX = SIZE / 2;
  const CY = SIZE / 2;
  // Field radius matches the outer boundary of the JPG. Tuned by
  // visual inspection — the outer field circle sits ~46% from centre.
  const FIELD_R = SIZE * 0.46;

  const runColor = (r) => r >= 6 ? "#fb7185" : r >= 4 ? "#60a5fa" : r >= 2 ? "#4ade80" : r >= 1 ? "#facc15" : "#d4d4d8";

  const looksUnit = points.length > 0 && (() => {
    const ax = Math.abs(Number(points[0][4] || 0));
    const ay = Math.abs(Number(points[0][5] || 0));
    return ax <= 1.5 && ay <= 1.5;
  })();
  // 2026-06-25 — flip the y direction. The wagon_wheel.jpg background
  // has Long On / Long Off (the "down the ground" sectors) at the
  // BOTTOM of the image, but our spatial coords convention had
  // positive y = down-the-ground rendered UPWARD in SVG (y = CY -
  // yUnit * FIELD_R). That sent every forward shot into the
  // Third Man / Fine Leg area at the TOP. Inverting the y-axis at
  // render time pushes every existing + future match's shots to the
  // correct half without re-backfilling Match.spatial.
  const toPx = (xUnit, yUnit) =>
    looksUnit
      ? [CX + xUnit * FIELD_R, CY + yUnit * FIELD_R]
      : [(xUnit - 190) * (FIELD_R / 160) + CX, -(yUnit - 195) * (FIELD_R / 160) + CY];

  return (
    <svg
      viewBox={`0 0 ${SIZE} ${SIZE}`}
      className="w-full max-w-[420px] rounded-lg border border-border-default"
      style={{ imageRendering: "auto" }}
    >
      {/* Pre-rendered wagon-wheel background (sectors + JPG-baked
          field labels + pitch + stumps). NEVER mirrored — the JPG
          stays in its native RHB orientation so every label reads
          normally. For LHB views the SHOTS are what flip (see the
          rawX flip in the points loop below), not the field. */}
      <image
        href="/wagon_wheel.jpg"
        x={0} y={0} width={SIZE} height={SIZE}
        preserveAspectRatio="xMidYMid slice"
      />
      {/* Boundary layer — highlighted so the field edge reads clearly
          even with 100+ shot lines overlaid. Double-stroked: a soft
          yellow halo behind a crisp yellow ring. */}
      <circle
        cx={CX} cy={CY} r={FIELD_R}
        fill="none"
        stroke="rgba(250,204,21,0.35)"
        strokeWidth={6}
      />
      <circle
        cx={CX} cy={CY} r={FIELD_R}
        fill="none"
        stroke="#fde047"
        strokeWidth={2.5}
      />
      {/* Shot lines + endpoints. For LHB, the rawX is flipped so the
          shot lands in the visually-equivalent sector under the RHB
          labels — e.g. an LHB cover-drive (off-side for the LHB)
          appears in the JPG's "Mid Wicket" sector, which the operator
          mentally reads as "off side for the left-hander". JPG labels
          stay readable; the field positions effectively swap roles. */}
      <ShotsGroup
        points={points}
        mirrorX={mirrorX}
        CX={CX} CY={CY} FIELD_R={FIELD_R}
        toPx={toPx}
        runColor={runColor}
      />
    </svg>
  );
}


// Shots renderer. For LHB views we flip rawX so the shot lands in the
// JPG sector that visually mirrors the batter's natural off / leg side —
// the JPG itself stays in RHB orientation with readable labels.
function ShotsGroup({ points, mirrorX, CX, CY, FIELD_R, toPx, runColor }) {
  return (
    <>
      {points.map((b, i) => {
        const runs = Number(b[2] || 0);
        const wkt  = b[3] === 1;
        let rawX = Number(b[4] || 0);
        const rawY = Number(b[5] || 0);
        if (mirrorX) rawX = -rawX;
        let [x, y] = toPx(rawX, rawY);
        const dx = x - CX, dy = y - CY;
        const dist = Math.hypot(dx, dy) || 1;
        // Force 4s and 6s to land ON the boundary; clamp everything
        // else inside the field circle.
        let target = dist;
        if (runs >= 4 && !wkt) target = FIELD_R;
        else if (dist > FIELD_R) target = FIELD_R;
        x = CX + (dx / dist) * target;
        y = CY + (dy / dist) * target;
        const c = runColor(runs);
        const sz = runs >= 6 ? 4 : runs >= 4 ? 3.5 : runs >= 1 ? 2.8 : 2.4;
        if (wkt) return (
          <g key={i}>
            <line x1={CX} y1={CY} x2={x} y2={y} stroke="#ef4444" strokeWidth={1.2} opacity={0.95} />
            <circle cx={x} cy={y} r={sz + 1.2} fill="none" stroke="#ef4444" strokeWidth={1.5} />
            <line x1={x - sz} y1={y - sz} x2={x + sz} y2={y + sz} stroke="#ef4444" strokeWidth={1.2} />
            <line x1={x + sz} y1={y - sz} x2={x - sz} y2={y + sz} stroke="#ef4444" strokeWidth={1.2} />
          </g>
        );
        return (
          <g key={i}>
            <line x1={CX} y1={CY} x2={x} y2={y} stroke={c}
                  strokeWidth={runs >= 4 ? 1.2 : 0.85}
                  opacity={runs === 0 ? 0.55 : 0.85}
                  strokeLinecap="round" />
            <circle cx={x} cy={y} r={sz} fill={c}
                    fillOpacity={0.95}
                    stroke="rgba(0,0,0,0.55)" strokeWidth={0.4} />
          </g>
        );
      })}
    </>
  );
}

// ── PITCH MAP ────────────────────────────────────────────────────────────────

// Pitch oriented with BATTER'S END at the top. Rows top → bottom:
//   B  (Beamer)        — behind batter stumps (top-most)
//   FT (Full Toss)     — behind batter stumps
//   Y  (Yorker)        — at batter stumps
//   HV (Full / Half Volley)
//   LB (Length)
//   BOL (Back of Length)
//   SH (Short)
//   HT (Half Tracker)  — closest to bowler (bottom-most)
const LEN_ORDER = ["B", "FT", "Y", "HV", "LB", "BOL", "SH", "HT"];
const LEN_LABEL = { Y: "Yorker", FT: "Full Toss", HV: "Full", LB: "Length", BOL: "Back of Length", SH: "Short", HT: "Half Tracker", B: "Beamer" };
const LINE_ORDER = ["WOO", "OO", "OS", "MS", "LS", "DL", "WDL"];
// Short codes per user request — easier to fit across a narrow pitch.
// Hover tooltips still expand to the full description.
const LINE_LABEL = { WOO: "WOO", OO: "OO", OS: "OS", MS: "MS", LS: "LS", DL: "DL", WDL: "WDL" };
const LINE_TITLE = { WOO: "Wide Outside Off", OO: "Outside Off", OS: "Off Stump", MS: "Middle Stump", LS: "Leg Stump", DL: "Down Leg", WDL: "Wide Down Leg" };

function InningsReconciliation({ inn, offBatPlotted }) {
  if (!inn) return null;
  const teamName = inn.batting_team?.name || inn.batting_team_name
    || `Innings ${inn.innings_number}`;
  return (
    <div className="bg-bg-secondary/40 border border-border-default rounded-lg p-3 text-[11px] mb-3">
      <div className="font-semibold mb-1">{teamName}</div>
      <div className="grid grid-cols-2 gap-y-0.5 text-text-secondary">
        <span>Off-bat (plotted)</span>
        <span className="text-right font-mono text-text-primary">{offBatPlotted}</span>
      </div>
    </div>
  );
}


function PitchMapSection({ spatial, innings }) {
  const pm = spatial?.pm || [];
  const bw = spatial?.bw || [];
  // innings prop kept on the signature for callsite compatibility; the
  // reconciliation card was removed at user request, but other future
  // panels may want it.
  void innings;
  const [innFilter, setInnFilter] = useState("all");
  const [bowler, setBowler] = useState("all");
  const [view, setView] = useState("all");

  // Interconnected filter helpers — same shape as wagon section.
  const passInnings = (b) =>
    innFilter === "all" || String(b[0]) === String(innFilter);
  const passBowler = (b) =>
    bowler === "all" || (bw[b[1]] || "") === bowler;
  const passView = (b) => view !== "wickets" || b[3] === 1;

  if (!pm.length) {
    return (
      <Section title="Pitch map" right={<span className="text-[10px] text-amber-300 uppercase tracking-wider">No spatial source</span>}>
        <p className="text-xs text-text-secondary">No pitch-map points in the source for this fixture.</p>
      </Section>
    );
  }

  const innOptions = useMemo(() => {
    const set = new Set(pm.map((b) => b[0]).filter((x) => x != null));
    return ["all", ...Array.from(set).sort()];
  }, [pm]);

  // Bowler options are scoped to the picked innings + view so the
  // dropdown only lists bowlers that have deliveries in the current view.
  const bowlerOptions = useMemo(() => {
    const seen = new Set();
    const list = [];
    pm.forEach((b) => {
      if (!passInnings(b)) return;
      if (!passView(b)) return;
      const nm = bw[b[1]];
      if (nm && !seen.has(nm)) { seen.add(nm); list.push(nm); }
    });
    list.sort();
    return ["all", ...list];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pm, bw, innFilter, view]);

  useEffect(() => {
    if (bowler !== "all" && !bowlerOptions.includes(bowler)) {
      setBowler("all");
    }
  }, [bowler, bowlerOptions]);

  const filtered = useMemo(() =>
    pm.filter((b) => {
      if (!passInnings(b)) return false;
      if (!passBowler(b)) return false;
      if (!passView(b)) return false;
      const len = String(b[4] || "").toUpperCase();
      const line = String(b[5] || "").toUpperCase();
      return LEN_ORDER.includes(len) && LINE_ORDER.includes(line);
    })
  // eslint-disable-next-line react-hooks/exhaustive-deps
  , [pm, bw, innFilter, bowler, view]);

  const grid = useMemo(() => {
    const g = {};
    LEN_ORDER.forEach((l) => { g[l] = {}; LINE_ORDER.forEach((li) => { g[l][li] = { balls: 0, runs: 0, wkts: 0 }; }); });
    filtered.forEach((b) => {
      const len = String(b[4]).toUpperCase();
      const line = String(b[5]).toUpperCase();
      if (g[len]?.[line]) {
        g[len][line].balls++;
        g[len][line].runs += Number(b[2] || 0);
        if (b[3] === 1) g[len][line].wkts++;
      }
    });
    return g;
  }, [filtered]);

  const max = useMemo(() => {
    let m = 0;
    LEN_ORDER.forEach((l) => LINE_ORDER.forEach((li) => { m = Math.max(m, grid[l][li].balls); }));
    return m || 1;
  }, [grid]);

  const totBalls = filtered.length;
  const totRuns = filtered.reduce((s, b) => s + Number(b[2] || 0), 0);
  const totWkts = filtered.filter((b) => b[3] === 1).length;

  return (
    <Section title="Pitch map" icon={<Filter className="w-4 h-4" />}>
      <div className="flex flex-wrap gap-3 mb-4">
        <FilterSelect label="Innings" value={innFilter} onChange={setInnFilter}
          options={innOptions.map((v) => ({ value: String(v), label: v === "all" ? "All innings" : `Innings ${v}` }))} />
        <FilterSelect label="Bowler" value={bowler} onChange={setBowler}
          options={bowlerOptions.map((nm) => ({ value: nm, label: nm === "all" ? "All bowlers" : nm }))} />
        <FilterSelect label="View" value={view} onChange={setView}
          options={[{ value: "all", label: "All balls" }, { value: "wickets", label: "Wickets only" }]} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-4">
        <PitchMapSVG grid={grid} max={max} />
        <div>
          {/* Length breakdown + innings reconciliation removed per user
              request — just the run totals stay. */}
          <div className="grid grid-cols-3 gap-2 text-xs">
            <Stat v={totBalls} l="Balls" />
            <Stat v={totRuns} l="Off-bat runs" />
            <Stat v={totWkts} l="Wickets" />
          </div>
        </div>
      </div>
    </Section>
  );
}

function PitchMapSVG({ grid, max }) {
  // Pitch dimensions returned to a normal/readable size — the very
  // compact 110x180 version made the heat cells too small to read
  // ball / wicket counts clearly.
  const PW = 170, PH = 290, OFFSET_X = 70, OFFSET_Y = 32;
  const cellW = PW / LINE_ORDER.length;
  const cellH = PH / LEN_ORDER.length;
  // Wider opacity range so 1 ball is visible (was washed out) and a hot
  // cell (50+ balls) reads clearly.
  const heat = (n) => {
    if (!n) return "rgba(0,0,0,0.20)";
    const t = Math.min(1, n / max);
    return `rgba(229,57,53,${(0.20 + 0.72 * t).toFixed(2)})`;
  };
  return (
    <svg viewBox={`0 0 ${PW + OFFSET_X + 55} ${PH + OFFSET_Y + 50}`} className="w-full max-w-[340px] rounded-lg border border-border-default" style={{ background: "#1a3c1a" }}>
      {/* Pitch background */}
      <rect x={OFFSET_X} y={OFFSET_Y} width={PW} height={PH} fill="#c4a27a" rx={4} />
      {/* Cells */}
      {LEN_ORDER.map((len, li) => LINE_ORDER.map((line, lii) => {
        const cell = grid[len][line];
        const x = OFFSET_X + lii * cellW, y = OFFSET_Y + li * cellH;
        const c = cell.balls > 0 ? heat(cell.balls) : "rgba(0,0,0,0.18)";
        return (
          <g key={`c${len}${line}`}>
            <rect x={x} y={y} width={cellW} height={cellH} fill={c} stroke="rgba(0,0,0,0.2)" strokeWidth={0.5} />
            {cell.balls > 0 && <text x={x + cellW / 2} y={y + cellH / 2 - 2} textAnchor="middle" fill={cell.balls / max > 0.5 ? "white" : "#222"} fontSize={11} fontFamily="monospace">{cell.balls}</text>}
            {cell.wkts > 0 && <text x={x + cellW / 2} y={y + cellH / 2 + 11} textAnchor="middle" fill="#ef4444" fontSize={9} fontFamily="monospace">{cell.wkts}W</text>}
          </g>
        );
      }))}
      {/* Length labels (left). LEN_ORDER runs top → bottom: Beamer
          (behind batter stumps) at the top, Half Tracker (closest to
          bowler) at the bottom. */}
      {LEN_ORDER.map((len, i) => <text key={`yl${len}`} x={OFFSET_X - 6} y={OFFSET_Y + i * cellH + cellH / 2 + 4} textAnchor="end" fill="rgba(255,255,255,0.85)" fontSize={9} fontWeight="500">{LEN_LABEL[len]}</text>)}
      {/* Line labels (top) — short codes per user request, with
          hover-tooltips for the long form. */}
      {LINE_ORDER.map((line, i) => (
        <text
          key={`xl${line}`}
          x={OFFSET_X + i * cellW + cellW / 2}
          y={OFFSET_Y - 8}
          textAnchor="middle"
          fill="rgba(255,255,255,0.9)"
          fontSize={10} fontWeight="600"
        >
          <title>{LINE_TITLE[line]}</title>
          {LINE_LABEL[line]}
        </text>
      ))}
      {/* End labels — batter at top, bowler at bottom. Stumps drawn
          inside the image already; we don't add overlay sticks (user
          asked to remove the painted stumps). */}
      <text x={OFFSET_X + PW / 2} y={OFFSET_Y - 22} textAnchor="middle" fill="rgba(255,255,255,0.9)" fontSize={10} fontWeight="600">Batter's end</text>
      <text x={OFFSET_X + PW / 2} y={OFFSET_Y + PH + 22} textAnchor="middle" fill="rgba(255,255,255,0.9)" fontSize={10} fontWeight="600">Bowler's end</text>
    </svg>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
function FilterSelect({ label, value, onChange, options }) {
  return (
    <div className="flex flex-col">
      <label className="text-[10px] uppercase tracking-wider text-text-secondary mb-1">{label}</label>
      <select value={value} onChange={(e) => onChange(e.target.value)} className="bg-bg-main border border-border-default rounded-md py-1.5 px-3 text-sm min-w-[180px] focus:outline-none focus:border-button-primary">
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}
function Stat({ v, l }) {
  return (
    <div className="bg-bg-secondary/40 border border-border-default rounded-lg p-2.5 text-center">
      <div className="text-base font-bold text-text-primary">{v}</div>
      <div className="text-[10px] uppercase tracking-wider text-text-secondary">{l}</div>
    </div>
  );
}

/* ── Offline pre-cache button ─────────────────────────────────────────────── */
function OfflineBundleToggle({ matchId }) {
  const { cached, busy, err, download, clear } = useOfflineBundle(matchId);
  if (cached) {
    return (
      <div className="flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md bg-emerald-500/10 border border-emerald-500/30 text-emerald-300">
        <CheckCircle2 className="w-3.5 h-3.5" />
        <span>Available offline</span>
        <button onClick={clear} className="text-emerald-300/70 hover:text-emerald-300" title="Remove offline copy">
          <Trash2 className="w-3 h-3" />
        </button>
      </div>
    );
  }
  return (
    <button
      onClick={download}
      disabled={busy}
      title="Cache match data so the scoring console works with no internet"
      className="text-sm px-3 py-1.5 rounded-md bg-bg-secondary border border-border-default hover:border-button-primary text-text-primary flex items-center gap-1.5 disabled:opacity-50"
    >
      <Download className="w-3.5 h-3.5" />
      {busy ? "Caching…" : "Make offline"}
      {err && <span className="text-red-400 ml-1">({err})</span>}
    </button>
  );
}

/* ── PDF download ────────────────────────────────────────────────────────── */
function PDFReportButton({ matchId }) {
  const [busy, setBusy] = useState(false);
  const pdfConfirm = useCreditConfirm("match_report_pdf", "match report PDF");

  const doDownload = async () => {
    setBusy(true);
    try {
      const r = await api.post(
        `/api/matches/${matchId}/report.pdf`,
        null,
        { responseType: "blob" },
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `match_${matchId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Defer revoke so the click finishes downloading first.
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      if (e?.response?.status !== 402) {
        alert(`PDF download failed: ${e?.response?.data?.detail || e.message}`);
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        onClick={() => pdfConfirm.confirm(doDownload)}
        disabled={busy}
        title="Download the structured scorecard as a printable PDF (innings tables, batting/bowling figures) — 5 Zencredits"
        className="text-sm px-3 py-1.5 rounded-md bg-bg-secondary border border-border-default hover:border-button-primary text-text-primary flex items-center gap-1.5 disabled:opacity-50"
      >
        <FileDown className="w-3.5 h-3.5" />
        {busy ? "Preparing…" : "Scorecard PDF · 5 ZC"}
      </button>
      <CreditConfirmModal {...pdfConfirm.modalProps} />
    </>
  );
}

/* ── AI Press Report (long-form, paid, PDF) ─────────────────────────────── */
function PressReportButton({ matchId }) {
  const [busy, setBusy] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const pressConfirm = useCreditConfirm("match_press_report", "AI Press Report");

  const doDownload = async () => {
    setBusy(true);
    setElapsed(0);
    // Surface a running counter while Sonnet writes the report — the call
    // legitimately takes 30-90s and the default 15s timeout used to fire
    // before generation finished. Counter resets on completion.
    const tick = setInterval(() => setElapsed((e) => e + 1), 1000);
    try {
      const r = await api.post(
        `/api/matches/${matchId}/press-report.pdf`,
        null,
        {
          responseType: "blob",
          // Sonnet 4.6 generates ~3500 tokens; expect 30-90s end-to-end
          // (LLM round-trip + WeasyPrint render). Allow 3 minutes so the
          // backend's 120s anthropic timeout has headroom + we don't fire
          // the global 15s axios timeout.
          timeout: 180000,
        },
      );
      const blob = new Blob([r.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `press_report_${matchId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (e) {
      if (e?.response?.status !== 402) {
        // Axios timeout error has no .response — give a friendlier message
        // than the raw "timeout of X exceeded" toast.
        const msg = e?.code === "ECONNABORTED"
          ? "Generation took longer than expected. Try once more — if the cache populated, the second click will be instant."
          : (e?.response?.data?.detail || e.message);
        alert(`Press report failed: ${msg}`);
      }
    } finally {
      clearInterval(tick);
      setBusy(false);
      setElapsed(0);
    }
  };

  return (
    <>
      <button
        onClick={() => pressConfirm.confirm(doDownload)}
        disabled={busy}
        title="Generate a detailed 10-section sports-journalist match report PDF (20 Zencredits — free re-downloads)"
        className="text-sm px-3 py-1.5 rounded-md bg-button-primary/10 border border-button-primary/40 hover:bg-button-primary/20 hover:border-button-primary text-button-primary flex items-center gap-1.5 disabled:opacity-50 font-medium"
      >
        <Newspaper className="w-3.5 h-3.5" />
        {busy
          ? (elapsed > 0 ? `Writing report… ${elapsed}s` : "Generating…")
          : "AI Press Report · 20 ZC"}
      </button>
      <CreditConfirmModal {...pressConfirm.modalProps} />
    </>
  );
}

/* ── AI auto-tag missing analytics ───────────────────────────────────────── */
function AITagButton({ matchId }) {
  const [busy, setBusy] = useState(false);
  const tagConfirm = useCreditConfirm("ai_tagging", "AI ball-event tagging");
  const doRun = async () => {
    setBusy(true);
    try {
      const r = await api.post(`/api/matches/${matchId}/ai-tag-balls/?fill_missing_only=true&max=200`);
      if (!r.data.error) alert(`Tagged ${r.data.tagged} balls (${r.data.skipped} skipped).`);
      else alert(`Error: ${r.data.error}`);
    } catch (e) {
      // 402 surfaced by the global modal; only show inline for other errors
      if (e?.response?.status !== 402) {
        alert(`Failed: ${e?.response?.data?.detail || e.message}`);
      }
    } finally {
      setBusy(false);
    }
  };
  const run = () => tagConfirm.confirm(doRun);
  return (
    <>
      <button
        onClick={run}
        disabled={busy}
        className="text-sm px-3 py-1.5 rounded-md bg-bg-secondary border border-border-default hover:border-button-primary text-text-primary flex items-center gap-1.5 disabled:opacity-50"
        title="Use Claude to fill missing per-ball analytics fields based on commentary + context"
      >
        <Sparkles className="w-3.5 h-3.5" />
        {busy ? "Tagging…" : "AI auto-tag"}
      </button>
      <CreditConfirmModal {...tagConfirm.modalProps} />
    </>
  );
}

/* ── Live spectator-feed indicator ────────────────────────────────────────── */
function LiveSubscribeIndicator({ matchId, matchState }) {
  // Only attempt live sub when match is in progress
  const enabled = matchState === "IN_PROGRESS";
  const live = useLiveMatch({ matchId, autoConnect: enabled });
  if (!enabled) return null;
  return (
    <div className={`flex items-center gap-1.5 text-xs px-2.5 py-1.5 rounded-md border ${
      live.connected ? "bg-red-500/10 border-red-500/40 text-red-300 animate-pulse"
                     : "bg-bg-secondary border-border-default text-text-secondary"
    }`}>
      <Wifi className="w-3.5 h-3.5" />
      <span className="uppercase tracking-wider text-[10px]">
        {live.connected ? "● LIVE" : "Connecting…"}
      </span>
      {live.latest && (
        <span className="font-mono text-[10px] text-text-secondary">
          {live.latest.over} · {live.latest.score}
        </span>
      )}
    </div>
  );
}
